import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Icon from "../../../../Icon/Icon";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import { InvestigationItemContainer } from "../components/InvestigationItemContainer";
import {
  ACCOUNT_FLOWS,
  ACCOUNT_TYPES,
  formatAmountNumber,
  formatAmountWithCurrency,
  parseAmount,
  sanitizeAmountInput,
} from "../../../../../helpers/financialImpact";

/**
 * Panel plegable de la seccion. Sale cerrado por defecto: el impacto
 * financiero solo aplica a una parte de los casos y no debe robarse la vista.
 * Cuando ya hay algo capturado se anuncia con un chip en la cabecera.
 */
export const CollapsablePanel = ({
  icon,
  title,
  badge,
  badgeColor,
  defaultOpen = false,
  children,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box
      p={1.5}
      borderRadius="5px"
      border={`1px solid ${theme.palette.blueGray200.main}`}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={() => setOpen(!open)}
        sx={{ cursor: "pointer", userSelect: "none" }}
      >
        {icon && (
          <Icon name={icon} size={20} color={theme.palette.secondary.main} />
        )}
        <Typography
          fontSize={14}
          fontWeight={600}
          color={theme.palette.ethicsBlue.main}
        >
          {title}
        </Typography>
        {badge && (
          <Chip
            size="small"
            label={badge}
            sx={{
              fontSize: 11,
              height: 20,
              color: theme.palette.white.main,
              backgroundColor: badgeColor ?? theme.palette.secondary.main,
            }}
          />
        )}
        <Box flexGrow={1} />
        <Icon
          name="bold_arrow_down_3"
          size={12}
          color={theme.palette.blueGray500.main}
          style={{
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.2s ease-in-out",
          }}
        />
      </Stack>

      <Collapse in={open}>
        <Box pt={2}>{children}</Box>
      </Collapse>
    </Box>
  );
};

/**
 * Campo de monto. Mientras se escribe muestra el numero pelon (para no pelear
 * con el cursor) y al salir del campo lo formatea con separadores de miles.
 */
export const AmountField = ({ label, value, onChange, disabled, isValid }) => {
  const [draft, setDraft] = useState(null);
  const isEditing = draft !== null;

  return (
    <EGInput
      label={label}
      value={isEditing ? draft : formatAmountNumber(value)}
      iconName="dollar_circle"
      disabled={disabled}
      isValid={isValid}
      showHelpText={false}
      inputProps={{ inputMode: "decimal" }}
      onFocus={() => setDraft(value === null ? "" : String(value))}
      onChange={(e) => {
        const raw = sanitizeAmountInput(e.target.value);
        setDraft(raw);
        onChange(parseAmount(raw));
      }}
      onBlur={() => setDraft(null)}
    />
  );
};

/** Renglon "etiqueta / monto" con la franja de color a la izquierda. */
export const AmountRow = ({ label, amount, currency, color }) => {
  const theme = useTheme();

  if (amount === null || amount === undefined) return null;

  return (
    <InvestigationItemContainer
      color={color ?? theme.palette.ethicsBlue.main}
      sx={{ p: 1.2 }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 0, sm: 1 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Typography
          fontSize={13}
          fontWeight={600}
          color={theme.palette.blueGray600.main}
        >
          {label}
        </Typography>
        <Typography
          fontSize={16}
          fontWeight={700}
          color={color ?? theme.palette.ethicsBlue.main}
        >
          {formatAmountWithCurrency(amount, currency)}
        </Typography>
      </Stack>
    </InvestigationItemContainer>
  );
};

/** Color de cada flujo: el dinero sale de las afectadas y llega a las beneficiarias. */
export const useFlowColor = () => {
  const theme = useTheme();
  return (flow) =>
    flow === ACCOUNT_FLOWS.BENEFICIARY
      ? theme.palette.primary.main
      : theme.palette.red.main;
};

export const useFlowLabels = () => {
  const [t] = useTranslation("investigation");
  return {
    [ACCOUNT_FLOWS.AFFECTED]: t("financialImpact.flow_affected"),
    [ACCOUNT_FLOWS.BENEFICIARY]: t("financialImpact.flow_beneficiary"),
  };
};

export const useAccountTypeLabels = () => {
  const [t] = useTranslation("investigation");
  return {
    [ACCOUNT_TYPES.ACCOUNT]: t("financialImpact.account_type_account"),
    [ACCOUNT_TYPES.CLABE]: t("financialImpact.account_type_clabe"),
    [ACCOUNT_TYPES.CARD]: t("financialImpact.account_type_card"),
    [ACCOUNT_TYPES.OTHER]: t("financialImpact.account_type_other"),
  };
};
